import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, Select, Checkbox, Popconfirm, InputNumber, Alert, Divider, Tag, Typography } from "antd";
import { ContrastTag } from "../../../../components/shared";
import { SaveOutlined, DeleteOutlined, InfoCircleOutlined, CopyOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Template, createTemplate, deleteTemplate, updateTemplate } from "../../../../api/communications/templateApi";
import { fetchEmailSignatures, EmailSignature } from "../../../../api/communications/emailSignatureApi";
import { User } from "../../../../types/admin/UserTypes";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";
import DetailDrawer from "../../../../components/shared/DetailDrawer";
import { useNotifications } from "../../../../context/NotificationContext";

const { Text } = Typography;

interface TemplateBuilderDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	template: Template;
	isSubmitting: boolean;
	setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export const categoryOptions = [
	{ label: "Appointment Booking Link Notification", value: "appointment_booking_link_notification" },
	{ label: "Appointment Follow-up Notification", value: "appointment_follow_up_notification" },
	{ label: "Appointment Reminder Notification", value: "appointment_reminder_notification" },
	{ label: "Appointment Scheduled Notification", value: "appointment_scheduled_notification" },
	{ label: "Follow-up Reminder Notification", value: "followup_reminder_notification" },
	{ label: "Form Assigned Notification", value: "form_assigned_notification" },
	{ label: "Gene.Codes Login Notification", value: "gene_codes_available_notification" },
	{ label: "Invoice Sent Notification", value: "invoice_sent_notification" },
	{ label: "Kit Registration Notification", value: "kit_registration_notification" },
	{ label: "Manual Notification", value: "send_manual_notification" },
	{ label: "Methylation Report Ready Notification", value: "methylation_report_ready_notification" },
	{ label: "New Patient Notification", value: "new_patient_notification" },
	{ label: "New Resources Uploaded", value: "result_emailed_notification" },
	{ label: "Payment Failed Notification", value: "payment_failed_notification" },
	{ label: "Payment Receipt Notification", value: "payment_receipt_notification" },
	{ label: "Payment Reminder Notification", value: "payment_reminder_notification" },
	{ label: "Sample Received Notification", value: "sample_received_notification" },
];

export const userAttributes = [
	{ label: "Address", value: "address" },
	{ label: "Appointment Date", value: "appointment_date" },
	{ label: "Appointment Time", value: "appointment_time" },
	// The abbreviation, e.g. `AEDT` — the one a patient-facing message wants.
	{ label: "Appointment Timezone", value: "appointment_timezone" },
	// ⚠️ The IANA name, e.g. `Australia/Melbourne`. Unlike the abbreviation above, ONLY the
	// appointment REMINDER path sends this — on a booking confirmation or a treatment-plan
	// notification it renders as the literal `{{appointment_timezone_full}}`.
	{ label: "Appointment Timezone (IANA)", value: "appointment_timezone_full" },
	{ label: "Appointment Type", value: "appointment_type" },
	// The tokenised patient booking URL. Must stay spelled `booking_link` — it is the key
	// `utils/healthcare/bookingLinkNotification.ts` puts in the `data` bag, and the notification
	// service resolves merge tags by a naive `{{key}}` string replace against that bag.
	{ label: "Booking Link", value: "booking_link" },
	{ label: "City", value: "city" },
	{ label: "Company", value: "company" },
	{ label: "Country", value: "country" },
	{ label: "Date of Birth", value: "date_of_birth" },
	{ label: "Email", value: "email" },
	{ label: "First Name", value: "first_name" },
	// The tokenised patient form URL. Must stay spelled `form_link` — it is the key
	// `utils/forms/formAssignedNotification.ts` puts in the `data` bag, and the notification
	// service resolves merge tags by a naive `{{key}}` string replace against that bag.
	{ label: "Form Link", value: "form_link" },
	{ label: "Gender", value: "gender" },
	{ label: "Last Name", value: "last_name" },
	// ⚠️ Unlike the patient-profile attributes around it, NOTHING populates this server-side.
	// `utils/healthcare/bookingLinkNotification.ts` is the only sender that puts `expires_at` in
	// the `data` bag, so in a template sent by any other flow it renders as the literal
	// `{{expires_at}}` — and nothing errors anywhere.
	{ label: "Link Expiry", value: "expires_at" },
	{ label: "Middle Name", value: "middle_name" },
	{ label: "Password", value: "password" },
	{ label: "Phone", value: "phone" },
	{ label: "Postal Code", value: "postal_code" },
	{ label: "Profile Picture", value: "profile_picture" },
	{ label: "Sex", value: "sex" },
	{ label: "State", value: "state" },
	{ label: "Username", value: "username" },
	{ label: "Booking Start", value: "booking_start" },
	{ label: "Booking End", value: "booking_end" },
	{ label: "Consultation Link", value: "consultation_link" },
	{ label: "Patient Login Link", value: "patient_login_link" },
	{ label: "Practitioner Name", value: "practitioner_name" },
	{ label: "Test Assignment Link", value: "test_assignment_link" },
	// ── Payment notifications (N5, 86d3uvwug) ────────────────────────────────────────────────
	// APPENDED, not merged alphabetically, so this block stays diff-identical to the admin
	// builder's (`actionable-frontend` GlobalNotificationBuilderPage.tsx) and the two can be
	// reviewed side by side.
	//
	// ⚠️ These — with `first_name`, `last_name`, `company` and `patient_login_link` above, the last
	// two filled by notification from the clinic's record — are the ONLY keys the finance service
	// may send, and unlike the rest of this list the notification service ENFORCES it:
	// `PaymentNotificationDataGuard` rejects a payment payload carrying anything else with a 422 at
	// send time. Two consequences for a template author:
	//   • the last four groups are per-category. A `{{days_overdue}}` typed into a receipt
	//     template renders a literal tag, because finance may not send that key on a receipt.
	//   • a tag typed on a NON-payment template renders literally too, exactly as it always has —
	//     nothing populates these outside the four payment categories.
	{ label: "Patient Name", value: "patient_name" },
	{ label: "Invoice Number", value: "invoice_number" },
	{ label: "Invoice Date", value: "invoice_date" },
	{ label: "Due Date", value: "due_date" },
	{ label: "Invoice Total", value: "invoice_total" },
	{ label: "Subtotal (ex GST)", value: "subtotal_ex_gst" },
	{ label: "GST Amount", value: "gst_amount" },
	{ label: "Amount Due", value: "amount_due" },
	{ label: "Amount Paid", value: "amount_paid" },
	{ label: "Currency", value: "currency" },
	{ label: "Clinic Name", value: "clinic_name" },
	{ label: "Clinic ABN", value: "clinic_abn" },
	{ label: "Clinic Email", value: "clinic_email" },
	{ label: "Clinic Phone", value: "clinic_phone" },
	{ label: "Invoice Pay Link", value: "invoice_pay_link" },
	// Invoice Sent only
	{ label: "Payment Terms", value: "payment_terms" },
	{ label: "Invoice Line Summary", value: "invoice_line_summary" },
	// Payment Receipt only
	{ label: "Payment Amount", value: "payment_amount" },
	{ label: "Payment Date", value: "payment_date" },
	{ label: "Payment Method", value: "payment_method" },
	{ label: "Payment Reference", value: "payment_reference" },
	{ label: "Card Last 4", value: "card_last4" },
	{ label: "Receipt Number", value: "receipt_number" },
	{ label: "Balance Remaining", value: "balance_remaining" },
	// Payment Failed only
	{ label: "Failure Reason", value: "failure_reason" },
	{ label: "Retry Link", value: "retry_link" },
	{ label: "Attempt Number", value: "attempt_number" },
	// Payment Reminder only
	{ label: "Days Overdue", value: "days_overdue" },
	{ label: "Reminder Sequence", value: "reminder_sequence" },
];

const TemplateBuilderDrawer: React.FC<TemplateBuilderDrawerProps> = ({ isOpen, onClose, template, isSubmitting, setIsSubmitting }) => {
	const { showSuccess, showError } = useNotifications();
	const [updatedTemplate, setUpdatedTemplate] = useState<Template>(template);
	const [editorContent, setEditorContent] = useState<string>("");
	const [editorKey, setEditorKey] = useState<number>(0);
	const [signatures, setSignatures] = useState<EmailSignature[]>([]);
	const quillRef = useRef<ReactQuill>(null);

	useEffect(() => {
		const load = async () => {
			if (isOpen) {
				setUpdatedTemplate(template);

				setEditorKey((prev) => prev + 1);

				setTimeout(() => {
					setEditorContent(template.body || "");
				}, 50);

				// Load signatures
				const user = getSessionUser() as User;
				let company = await fetchClinicCompany();

				if (company) {
					loadSignatures(company.id);
				}
			} else {
				setEditorContent("");
			}
		};
		load();
	}, [isOpen, template]);

	const loadSignatures = async (companyId: number) => {
		try {
			const signaturesData = await fetchEmailSignatures(companyId);
			setSignatures(signaturesData);
		} catch (error) {
			console.error("Error fetching email signatures:", error);
		}
	};

	const handleSave = async () => {
		if (updatedTemplate.is_follow_up) {
			if (!updatedTemplate.follow_up_delay_hours && !updatedTemplate.follow_up_delay_days) {
				showError("Follow-up template must have delay hours or days specified");
				return;
			}
		}

		setIsSubmitting(true);
		try {
			const templateToSave = {
				...updatedTemplate,
				body: editorContent,
			};

			if (templateToSave.id === 0) {
				await createTemplate(templateToSave);
				showSuccess("Template created successfully");
			} else {
				await updateTemplate(templateToSave);
				showSuccess("Template updated successfully");
			}
			onClose();
		} catch (error: unknown) {
			console.error(error);
			showError("Error saving template");
		}
		setIsSubmitting(false);
	};

	const handleDelete = async () => {
		setIsSubmitting(true);
		try {
			if (updatedTemplate.id !== 0) {
				await deleteTemplate(updatedTemplate.id);
				showSuccess("Template deleted successfully");
			}
			onClose();
		} catch (error: unknown) {
			console.error(error);
			showError("Error deleting template");
		}
		setIsSubmitting(false);
	};

	const handleInputChange = (field: string, value: any) => {
		setUpdatedTemplate((prev) => ({ ...prev, [field]: value }));
	};

	const handleEditorChange = (content: string) => {
		setEditorContent(content);
	};

	const handleInsertAttribute = (attribute: string) => {
		if (quillRef.current) {
			const quill = quillRef.current.getEditor();
			const range = quill.getSelection();
			const position = range ? range.index : quill.getLength();
			const token = `{{${attribute}}}`;
			quill.insertText(position, token);
			quill.setSelection(position + token.length);
		}
	};

	const modules = {
		toolbar: [[{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"], [{ list: "ordered" }, { list: "bullet" }], [{ color: [] }, { background: [] }], ["link", "image"], ["clean"]],
	};

	const getTotalDelayDescription = () => {
		const hours = updatedTemplate.follow_up_delay_hours || 0;
		const days = updatedTemplate.follow_up_delay_days || 0;

		const parts = [];
		if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
		if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);

		if (parts.length === 0) return "Not set";
		return parts.join(" and ") + " after appointment end";
	};

	const signatureOptions = signatures.map((sig) => ({
		label: sig.is_default ? `${sig.name} (Default)` : sig.name,
		value: sig.id,
	}));

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			title={updatedTemplate.name ? `Edit: ${updatedTemplate.name}` : "New Notification Template"}
			subtitle={updatedTemplate.category?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "Configure the template settings and compose the body"}
			width="85vw"
			loading={isSubmitting && !updatedTemplate.id}
			noPadding
			headerActions={
				<Space size={8}>
					{updatedTemplate.id !== 0 && (
						<Popconfirm
							title="Delete template"
							description="Are you sure you want to delete this template?"
							onConfirm={handleDelete}
							okText="Yes"
							cancelText="No"
							disabled={updatedTemplate.is_global}
						>
							<Button danger icon={<DeleteOutlined />} loading={isSubmitting} disabled={updatedTemplate.is_global}>
								Delete
							</Button>
						</Popconfirm>
					)}
					<Button type="primary" icon={<SaveOutlined />} loading={isSubmitting} onClick={handleSave} disabled={updatedTemplate.is_global}>
						Save Template
					</Button>
				</Space>
			}
		>
			<div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
				{/* Left: Attributes sidebar */}
				<div
					style={{
						width: 230,
						flexShrink: 0,
						borderRight: "1px solid var(--mg-border)",
						background: "var(--mg-bg-elevated)",
						overflowY: "auto",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<div
						style={{
							padding: "12px 16px 8px",
							borderBottom: "1px solid var(--mg-border)",
						}}
					>
						<Text strong style={{ fontSize: 13, color: "var(--mg-text)" }}>
							User Attributes
						</Text>
					</div>
					<div style={{ flex: 1, overflowY: "auto" }}>
						{userAttributes.map((attr) => (
							<div
								key={attr.value}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "7px 12px",
									borderBottom: "1px solid var(--mg-border)",
									cursor: "pointer",
									fontSize: 12,
									color: "var(--mg-text)",
									transition: "background 0.15s",
								}}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLElement).style.background = "var(--mg-primary-bg)";
									(e.currentTarget as HTMLElement).style.color = "var(--mg-primary)";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLElement).style.background = "";
									(e.currentTarget as HTMLElement).style.color = "var(--mg-text)";
								}}
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => handleInsertAttribute(attr.value)}
							>
								<span>{attr.label}</span>
								<CopyOutlined style={{ fontSize: 11, opacity: 0.5 }} />
							</div>
						))}
					</div>
					<div
						style={{
							padding: "10px 12px",
							background: "var(--mg-bg-subtle)",
							borderTop: "1px solid var(--mg-border)",
							fontSize: 11,
							color: "var(--mg-text-secondary)",
							lineHeight: 1.4,
						}}
					>
						💡 Click an attribute to insert it at your cursor position in the template body
					</div>
				</div>

				{/* Right: Form + editor */}
				<div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
					<Space direction="vertical" size={14} style={{ width: "100%" }}>
						{updatedTemplate.is_global && (
							<Alert
								type="warning"
								showIcon
								message="This is a global template managed by the Xoe Med admin system. Global templates cannot be edited or deleted. Create a new template in this category and activate it to override this one."
							/>
						)}
						{/* Core fields grid */}
						<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Name *</label>
								<Input
									placeholder="Enter template name"
									value={updatedTemplate.name}
									onChange={(e) => handleInputChange("name", e.target.value)}
								/>
							</div>

							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Type *</label>
								<Select
									value={updatedTemplate.type}
									onChange={(value) => handleInputChange("type", value)}
									style={{ width: "100%" }}
									options={[
										{ label: "Email", value: "email" },
										{ label: "SMS", value: "sms" },
										{ label: "Both", value: "both" },
									]}
								/>
							</div>

							{(updatedTemplate.type === "email" || updatedTemplate.type === "both") && (
								<div>
									<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Subject</label>
									<Input
										placeholder="Enter email subject"
										value={updatedTemplate.subject || ""}
										onChange={(e) => handleInputChange("subject", e.target.value)}
									/>
								</div>
							)}

							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Category</label>
								<Select
									value={updatedTemplate.category}
									onChange={(value) => handleInputChange("category", value)}
									style={{ width: "100%" }}
									options={categoryOptions}
									showSearch
									filterOption={(input, option) =>
										(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
									}
								/>
							</div>

							{(updatedTemplate.type === "email" || updatedTemplate.type === "both") && signatures.length > 0 && (
								<div>
									<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Email Signature</label>
									<Select
										value={updatedTemplate.email_signature_id}
										onChange={(value) => handleInputChange("email_signature_id", value)}
										style={{ width: "100%" }}
										options={signatureOptions}
										placeholder="Select email signature (optional)"
										allowClear
									/>
								</div>
							)}

							<div style={{ display: "flex", alignItems: "center", gap: 20, paddingTop: 22 }}>
								<Checkbox
									checked={updatedTemplate.is_active || false}
									onChange={(e) => handleInputChange("is_active", e.target.checked)}
								>
									<span style={{ fontSize: 12 }}>Active</span>
								</Checkbox>
								<Checkbox
									checked={updatedTemplate.is_follow_up || false}
									onChange={(e) => {
										handleInputChange("is_follow_up", e.target.checked);
										if (!e.target.checked) {
											handleInputChange("follow_up_delay_hours", undefined);
											handleInputChange("follow_up_delay_days", undefined);
										}
										if (e.target.checked) handleInputChange("is_reminder", false);
									}}
								>
									<span style={{ fontSize: 12 }}>Follow-up</span>
								</Checkbox>
								<Checkbox
									checked={updatedTemplate.is_reminder || false}
									onChange={(e) => {
										handleInputChange("is_reminder", e.target.checked);
										if (e.target.checked) {
											handleInputChange("is_follow_up", false);
											handleInputChange("follow_up_delay_hours", undefined);
											handleInputChange("follow_up_delay_days", undefined);
										}
									}}
								>
									<span style={{ fontSize: 12 }}>Reminder</span>
								</Checkbox>
							</div>
						</div>

						{/* Follow-up scheduling */}
						{updatedTemplate.is_follow_up && (
							<>
								<Divider style={{ margin: "4px 0" }} />
								<Alert
									message="Follow-up Scheduling"
									description="This template will be sent automatically after appointments. Configure when the notification should be sent below."
									type="info"
									icon={<InfoCircleOutlined />}
									showIcon
								/>
								<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
									<div>
										<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Delay (Days)</label>
										<InputNumber
											min={0}
											max={365}
											value={updatedTemplate.follow_up_delay_days}
											onChange={(value) => handleInputChange("follow_up_delay_days", value)}
											style={{ width: "100%" }}
											placeholder="e.g., 7"
										/>
									</div>
									<div>
										<label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "var(--mg-text-secondary)" }}>Delay (Hours)</label>
										<InputNumber
											min={0}
											max={168}
											value={updatedTemplate.follow_up_delay_hours}
											onChange={(value) => handleInputChange("follow_up_delay_hours", value)}
											style={{ width: "100%" }}
											placeholder="e.g., 24"
										/>
									</div>
								</div>
								<div
									style={{
										padding: "8px 12px",
										background: "var(--mg-info-bg, var(--mg-primary-bg))",
										border: "1px solid var(--mg-primary-border)",
										borderRadius: 6,
										fontSize: 12,
										color: "var(--mg-text)",
									}}
								>
									<strong>Scheduled for:</strong>{" "}
									<ContrastTag background="var(--mg-primary)" style={{ fontSize: 11, marginLeft: 4 }}>
										{getTotalDelayDescription()}
									</ContrastTag>
								</div>
							</>
						)}

						<Divider style={{ margin: "4px 0" }} />

						{/* Body editor */}
						<div>
							<label style={{ display: "block", marginBottom: 6, fontSize: 12, color: "var(--mg-text-secondary)" }}>Body</label>
							{isOpen && (
								<ReactQuill
									key={editorKey}
									ref={quillRef}
									theme="snow"
									value={editorContent}
									onChange={handleEditorChange}
									style={{ height: updatedTemplate.is_follow_up ? 300 : 400 }}
									modules={modules}
								/>
							)}
						</div>
					</Space>
				</div>
			</div>
		</DetailDrawer>
	);
};

export default TemplateBuilderDrawer;
